import { useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import {
  Asset,
  AuthorizedSignatory,
  ScheduleLine,
  UpfrontCharges,
  ScheduleConfig
} from "../../types/schema";
import SchedulePreviewModal from "../../components/schedules/SchedulePreviewModal";
import ScheduleGeneratorDrawer from "../../components/schedules/ScheduleGeneratorDrawer";
import EditSignatoriesModal from "../../components/schedules/EditSignatoriesModal";
import AssetManagementModal from "../../components/schedules/AssetManagementModal";
import SchedulePdfDrawer from "../../components/schedules/SchedulePdfDrawer";
import ScheduleDocumentDrawer from "../../components/schedules/ScheduleDocumentDrawer";
import ApproveScheduleModal, {
  ApproveScheduleConfirmPayload,
} from "../../components/schedules/ApproveScheduleModal";
import { determineGSTSplit } from "../../lib/gst-rules";
import { format, subDays, startOfDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../../store/appStore";
import { toast } from "react-toastify";
import { computeAssetValueTotals, generateScheduleLines } from "../../lib/calculations";
import apiService from "../../services/apiService";
import { useEffect } from "react";
import {
  Calendar,
  Clock,
  Repeat,
  TrendingUp,
  User,
  CheckCircle,
  Eye,
  FileText,
  UserCheck,
  Package,
  IndianRupee,
  Calculator,
  Shield,
  Building,
  Hash,
  MapPin,
  Percent,
  Receipt,
  Coins,
  BarChart3,
  Link2,
  FileCheck,
  Edit,
  Loader2,
  FileSignature,
} from "lucide-react";
import { formatDisplayDate, parseLocalDateInput } from "../../lib/dateFormat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { getInvoiceStatusColor, type InvoiceStatus } from "../../types/invoice";
import { getLocalStorageItem } from "../../utils";
import LeasingDocModal from "../../components/schedules/LeasingDocModal";
import ScheduleDocumentsTab from "../../components/schedules/ScheduleDocumentsTab";
import astrafinLogo from "../../assets/logo_two.svg";

interface AdminUserList {
  id: number;
  name: string;
}

interface Address {
  id: string;
  label: string;
  address: string;
  gstin?: string;
}


export default function ViewSchedule({
  schedule,
  customerAddresses,
  onBack,
  adminUserList,
  tdsRateList,
  onAddressesChange,
}: {
  schedule: any;
  customerAddresses: Address[] | null;
  onBack?: () => void;
  adminUserList: AdminUserList[];
  tdsRateList: any[];
  onAddressesChange?: (addresses: Address[]) => void;
}) {
  const navigate = useNavigate();
  const { updateSchedule } = useAppStore();

  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [editSignatoriesOpen, setEditSignatoriesOpen] = useState(false);
  const [assetManagementOpen, setAssetManagementOpen] = useState(false);
  const [systemDocDrawerOpen, setSystemDocDrawerOpen] = useState(false);
  const [uploadedDocDrawerOpen, setUploadedDocDrawerOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<
    "rental_schedule" | "power_of_attorney" | "ownership_acknowledgement" | null
  >(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [billToAddress, setBillToAddress] = useState<Address | null>(
    schedule?.address?.billTo || null
  );

  // Edit schedule drawer states
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editPreviewOpen, setEditPreviewOpen] = useState(false);
  const [editLines, setEditLines] = useState<ScheduleLine[] | null>(null);
  const [editUpfront, setEditUpfront] = useState<UpfrontCharges | null>(null);
  const [editMeta, setEditMeta] = useState<{
    config: ScheduleConfig;
    assets: Asset[];
  } | null>(null);
  const [termSheetId, setTermSheetId] = useState<string>("");
  const [termSheetList, setTermSheetList] = useState<any[]>([]);
  const [purchaseOrderAssetList, setPurchaseOrderAssetList] = useState<Asset[]>(
    []
  );
  const [leasingOpen, setLeasingOpen]= useState(false);
  const [calculatingPortfolio, setCalculatingPortfolio] = useState(false);
  const [portfolioCalculated, setPortfolioCalculated] = useState(
    () => Boolean(schedule?.portfolioCalculated ?? schedule?.portfolio_calculated)
  );

  useEffect(() => {
    setPortfolioCalculated(
      Boolean(schedule?.portfolioCalculated ?? schedule?.portfolio_calculated)
    );
  }, [schedule]);

  const relatedNoA = schedule?.noa;


  // If no schedule found, use mock data (fallback)
  let scheduleData = schedule || null;
  // Safely extract properties with fallbacks
  const lines = scheduleData?.lines || [];
  const upfront = scheduleData?.upfront || null;
  const config = scheduleData?.config;
  const rawAssets = scheduleData?.assets ?? scheduleData?.asset ?? [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "secondary";
      case "Submitted": return "warning";
      case "Approved": return "warning";
      case "In Signing": return "info";
      case "Assigned": return "success";
      case "Matured": return "destructive";
      case "Active": return "success";
      default: return "secondary";
    }
  };

  // Distinct productType and requirementId from schedule assets (for Rental Schedule Info)
  const distinctProductTypes = useMemo(() => {
    const values = (rawAssets as any[])
      .map((a: any) => a.productType ?? a.productTypeName)
      .filter((v: any) => v != null && v !== "");
    return [...new Set(values)];
  }, [rawAssets]);
  const distinctRequirementIds = useMemo(() => {
    const values = (rawAssets as any[])
      .map((a: any) => a.requirementId)
      .filter((v: any) => v != null && v !== "");
    return [...new Set(values)];
  }, [rawAssets]);

  // Group assets by assetLineId and invoiceNo, and sum base, total, and qty
  const assets = useMemo(() => {
    const grouped = rawAssets.reduce((acc: any, asset: any) => {
      const assetLineId = asset.assetLineId || asset.assetId || 'default';
      const invoiceNo = asset.invoiceNo || 'no-invoice';
      // Create a unique key combining assetLineId and invoiceNo
      const groupKey = `${assetLineId}_${invoiceNo}`;
      const base = asset.basePrice || 0;
      const qty = asset.quantity || 1;
      const total = base * qty * (1 + (asset.gstRate || 0) / 100);

      if (!acc[groupKey]) {
        acc[groupKey] = {
          ...asset,
          assetLineId,
          invoiceNo,
          baseSum: 0,
          totalSum: 0,
          qtySum: 0,
        };
      }

      acc[groupKey].baseSum += base;
      acc[groupKey].totalSum += total;
      acc[groupKey].qtySum += qty;

      return acc;
    }, {});

    // Convert grouped object to array and update the asset properties
    return Object.values(grouped).map((groupedAsset: any) => ({
      ...groupedAsset,
      basePrice: groupedAsset.baseSum,
      quantity: groupedAsset.qtySum,
      // Calculate total for display
      total: groupedAsset.totalSum,
    }));
  }, [rawAssets]);

  // Mock authorized signatories with state
  const [ourSignatory, setOurSignatory] = useState<AuthorizedSignatory>({
    name: config?.ourSignatory?.name || "",
    role: config?.ourSignatory?.role || "",
  });

  const [customerSignatory, setCustomerSignatory] =
    useState<AuthorizedSignatory>({
      name: config?.customerSignatory?.name || "",
      role: config?.customerSignatory?.role || "",
    });

  // Since we're using mock data, this condition won't trigger, but leaving for completeness
  if (!config) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="border rounded-lg p-6">
          <p>Schedule not found or data incomplete.</p>
          <Button onClick={() => window.history.back()} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }


  let baseTotal = 0;
  let tdsTotal = 0;
  let grandTotal = 0;
  let gstTotal = 0;

  lines.forEach((l: any) => {
    baseTotal += l.baseRentRounded || 0;
    tdsTotal += l.tdsRounded || 0;
    gstTotal += l.gstRaw || 0;
    grandTotal += l.baseRentRounded + (l.gstRaw ?? 0) || 0;
  });

  const gstTotals = {
    baseTotal,
    tdsTotal,
    gstTotal,
    grandTotal,
  };

  let baseAssetValue = 0;
  assets.forEach((asset: any) => {
    baseAssetValue += asset.basePrice * (asset.quantity || 1);
  });

  const [scheduleStatus, setScheduleStatus] = useState(
    scheduleData.status || "Draft"
  );
  const [approvedBy, setApprovedBy] = useState<string | null>(scheduleData.approvedBy);

  const handleApprove = () => {
    setApproveModalOpen(true);
  }

  const generateSelfFundedScheduleInvoices = (
    billTo: Address,
    scheduleLinesForInvoice: ScheduleLine[]
  ) => {
    const chkInterim = scheduleLinesForInvoice.some((line) => line.isInterim);
    const customer = scheduleData.config.customer;

    return scheduleLinesForInvoice.map((line) => {
      const baseAmount = line.baseRentRounded;
      const gstRawAmount = line.gstRaw;
      const tdsAmount = line.tdsRounded;
      const grandTotal = line.totalRaw;

      let displayPeriodNo: ScheduleLine["periodNo"] = line.periodNo;
      if (chkInterim) {
        if (line.periodNo === "i") displayPeriodNo = 1;
        else displayPeriodNo = Number(line.periodNo) + 1;
      }

      const gstSplit = determineGSTSplit(billTo.gstin ?? "", gstRawAmount);

      return {
        invoiceId: "1",
        displayId: displayPeriodNo,
        rsInvoiceCode: `INV-${schedule.id}-R${line.periodNo}`,
        scheduleId: schedule.id,
        customerId: customer.customerId,
        customer,
        type: line.isInterim ? "Advance" : "Rental",
        isManual: false,
        invoiceDate: (() => {
          if (!line.dueDate) return line.fromDate;
          const today = startOfDay(new Date());
          const due = startOfDay(new Date(line.dueDate));
          // 5-day window ending on due date: [due - 4 days, due]
          // e.g. due 5 Sep → window 1–5 Sep
          const windowStart = subDays(due, 4);

          if (today.getTime() < windowStart.getTime()) {
            return format(windowStart, "yyyy-MM-dd");
          }
          if (today.getTime() > due.getTime()) {
            return format(due, "yyyy-MM-dd");
          }
          return format(today, "yyyy-MM-dd");
        })(),
        dueDate: line.dueDate,
        baseRounded: baseAmount,
        gstRounded: Math.ceil(gstRawAmount),
        gstType: gstSplit.gstType,
        igstAmount: gstSplit.igst,
        cgstAmount: gstSplit.cgst,
        sgstAmount: gstSplit.sgst,
        utgstAmount: gstSplit.utgst,
        gstRate: line.gstRate,
        tdsRounded: tdsAmount,
        tdsRate: line.tds_rate ?? config.tdsRatePercent ?? config.TDS_rate ?? 2,
        grandTotalRounded: Math.ceil(grandTotal),
        totalRoundedAfterTDS: Math.ceil(line.totalRaw - line.tdsRounded),
        lineItems: [
          {
            id: `LINE-R${line.periodNo}-${schedule.id}`,
            scheduleID: schedule.id,
            scheduleLineID: line.scheduleLineId,
            description: `${line.isInterim ? "Interim " : ""}Rental - Period ${line.periodNo} (${line.fromDate} to ${line.toDate})`,
            baseAmount,
            gstAmount: gstRawAmount,
            tdsAmount,
            totalAmount: grandTotal,
            fromDate: line.fromDate,
            toDate: line.toDate,
          },
        ],
        payments: [],
        status: "Pending",
        assignedToLender: false,
        scheduleLineId: line.scheduleLineId,
      };
    });
  };

  const handleConfirmApproval = async (payload: ApproveScheduleConfirmPayload) => {
    const { address: billTo, assignmentMode, fundingDate, interimLine } = payload;

    setScheduleStatus("Approved");
    setBillToAddress(billTo);

    if (!schedule) return;

    try {
      await apiService.put(`/rental-schedule/update-rental-schedule-status`, {
        scheduleId: schedule.id,
        status: "Approved",
        lenderAssignment: assignmentMode === "later" ? "assign_later" : "assign_now",
        ...(assignmentMode === "later" && fundingDate ? { fundingDate } : {}),
      });

      await apiService.put(`/rental-schedule/update-rental-schedule-addresses`, {
        scheduleId: schedule.id,
        billTo,
      });

      if (assignmentMode === "later") {
        const nonInterimLines = lines.filter((l: ScheduleLine) => !l.isInterim);
        const scheduleLinesForInvoice: ScheduleLine[] = interimLine
          ? [interimLine, ...nonInterimLines]
          : nonInterimLines;

        const invoices = generateSelfFundedScheduleInvoices(billTo, scheduleLinesForInvoice);

        await apiService.post(`/rental-schedule/create-rental-schedule-invoices-assign-later`, {
          scheduleId: schedule.id,
          customerId: scheduleData.config.customer.customerId,
          invoices,
          fundingDate,
          interimLine: interimLine ?? null,
          assignmentMode: "assign_later",
          requirementId: distinctRequirementIds[0] ?? undefined,
        });

        toast.success(
          `Schedule approved. ${invoices.length} self-funded invoice(s) generated. Assign lender later via NoA.`
        );
      } else {
        toast.success("Schedule approved successfully. Assign lender via NoA to generate invoices.");
      }

      const userDetails = getLocalStorageItem("detail");
      const approverId = userDetails?.userId ?? userDetails?.id;
      setApprovedBy(approverId !== undefined && approverId !== null ? String(approverId) : null);

      // getInvoiceDetails();
    } catch (error) {
      console.error("Failed to update schedule:", error);
      toast.error("Failed to approve schedule. Please try again.");
      throw error;
    }
  };

  const handleVisualize = () => {
    setVisualizeOpen(true);
  };

  const handleCalculatePortfolio = async () => {
    if (!scheduleData?.id) {
      toast.error("Schedule not found.");
      return;
    }

    setCalculatingPortfolio(true);
    try {
      await apiService.post("/rental-schedule/calculate-portfolio", {
        scheduleId: scheduleData.id,
        calculatePortfolio: true,
      });
      setPortfolioCalculated(true);
      toast.success("Portfolio calculated successfully.");
    } catch (error) {
      console.error("Failed to calculate portfolio:", error);
      toast.error("Failed to calculate portfolio. Please try again.");
    } finally {
      setCalculatingPortfolio(false);
    }
  };

  const handleSaveSignatories = async (
    newOurSignatory: AuthorizedSignatory,
    newCustomerSignatory: AuthorizedSignatory
  ) => {
    setOurSignatory(newOurSignatory);
    setCustomerSignatory(newCustomerSignatory);
    scheduleData.config.ourSignatory = newOurSignatory;
    scheduleData.config.customerSignatory = newCustomerSignatory;

    if (!schedule?.id) return;
    try {
      await apiService.put(
        `/rental-schedule/update-rental-schedule-signatories`,
        {
          scheduleId: schedule.id,
          ourSignatory: newOurSignatory,
          customerSignatory: newCustomerSignatory,
        }
      );
      toast.success("Authorised signatories have been saved.");
    } catch (error) {
      console.error("Failed to update signatories", error);
      toast.error("Could not save signatories. Please try again.");
    }
  };

  const handleSaveAssets = (updatedAssets: Asset[]) => {
    if (!schedule) return;

    // Regenerate schedule lines with new assets
    const { lines: newLines, upfront: newUpfront } = generateScheduleLines(
      updatedAssets,
      schedule.config,
      true
    );

    // Update the schedule in the store
    updateSchedule(schedule.id, {
      assets: updatedAssets,
      lines: newLines,
      upfront: newUpfront,
    });

    toast.success("Schedule has been recalculated with new asset values.");
  };

  const openSystemDocDrawer = (
    docType:
      | "rental_schedule"
      | "power_of_attorney"
      | "ownership_acknowledgement"
  ) => {
    setSelectedDocType(docType);
    setSystemDocDrawerOpen(true);
  };

  const getUploadedDocument = (docType: any) => {
    return scheduleData.executedDocuments?.find(
      (d: any) => d.documentType === docType
    );
  };

  // const handleDocumentUpload = (docType: string, file: File) => {
  //   const document: ExecutedDocument = {
  //     documentId: `DOC-${Date.now()}`,
  //     documentType: docType as any,
  //     documentName: file.name,
  //     uploadedDate: new Date().toISOString(),
  //     uploadedBy: "Current User",
  //     fileUrl: URL.createObjectURL(file),
  //     fileName: file.name,
  //     fileSize: file.size,
  //     version: 1,
  //   };

  //   addExecutedDocument(scheduleData.id, document);
  // };

  // Fetch term sheet list and assets for editing
  useEffect(() => {
    if (scheduleData?.config?.customer?.customerId) {
      const customerId = scheduleData.config.customer.customerId;
      const scheduleId = scheduleData?.id;
      getTermSheetList(customerId);
      getPurchaseOrderAssetList(customerId, scheduleId);
    }
  }, [scheduleData]);

  const getTermSheetList = async (customerId: string) => {
    try {
      const response = await apiService.get<any>(
        `/customers/get-term-sheet/${customerId}`
      );
      const data = response.downloaded;
      setTermSheetList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch term sheet list:", error);
    }
  };

  const getPurchaseOrderAssetList = async (
    customerId: string,
    scheduleId: string
  ) => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/rs-get-purchase-order-asset-list/${customerId}?scheduleId=${scheduleId}`
      );
      const data = response.data;

      // Transform poNumber to PO-{last 4 digits}
      const transformedData = (Array.isArray(data) ? data : []).map(
        (item: Asset) => ({
          ...item,
          poNumber: item.poNumber
            ? `PO-${item.poNumber.slice(-4)}`
            : item.poNumber,
        })
      );

      setPurchaseOrderAssetList(transformedData);
    } catch (error) {
      console.error("Failed to fetch purchase order assets:", error);
    }
  };

  // const getInvoiceDetails = async () => {
  //   try {
  //     const response = await apiService.get<any>(
  //       `/purchase-order/get-invoice-details-count/${scheduleData.id}`
  //     );
  //     // const data = response.data;
  //     // setInvoiceDetails(data);
  //   }
  //   catch (error) {
  //     console.error("Failed to fetch invoice details:", error);
  //   }
  // };

  // useEffect(() => {
  //   getInvoiceDetails();
  // }, [scheduleData]);

  const handleEditSchedule = () => {
    setEditDrawerOpen(true);
  };

  const handleUpdateSchedule = async () => {
    if (editLines && editUpfront && editMeta && schedule) {
      try {
        const tdsRate =
          editMeta.config?.TDS_rate ??
          editMeta.config?.tdsRatePercent ??
          2;

        const { totalBaseAssetValue, totalAssetGst, totalAssetValue } =
          computeAssetValueTotals(editMeta.assets);

        const updatedSchedule = {
          customerId: String(schedule.config.customer.customerId || ""),
          scheduleId: schedule.id,
          lines: editLines,
          upfront: editUpfront,
          config: {
            ...editMeta.config,
            tdsRatePercent: tdsRate,
            TDS_rate: tdsRate,
          },
          assets: editMeta.assets,
          termSheetId: termSheetId,
          TDS_rate: tdsRate,
          totalBaseAssetValue,
          totalAssetGst,
          totalAssetValue,
        };

        const response = await apiService.post(
          `/rental-schedule/update-rental-schedule/${schedule.id}`,
          updatedSchedule
        );

        if (response) {
          toast.success("Schedule has been updated successfully.");
          setEditPreviewOpen(false);
          setEditDrawerOpen(false);
          // Refresh the page or reload schedule data
          if (onBack) {
            onBack();
          }
        }
      } catch (error) {
        console.error("Failed to update schedule:", error);
        toast.error("Failed to update schedule. Please try again.");
      }
    }
  };

  const formatScheduleNumber = (id: string | number | undefined): string => {
    if (!id) return "";
    const num = typeof id === "string" ? parseInt(id, 10) : id;
    if (isNaN(num)) return String(id);
    return num < 10 ? `0${num}` : String(num);
  };

  const formatOrdinal = (num: number | undefined): string => {
    if (num === undefined || num === null) return "";
    const n = Number(num);
    if (isNaN(n)) return String(num);

    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${n}th`;
    }

    switch (lastDigit) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  const [executedDocumentUrl, setExecutedDocumentUrl] = useState<string>("");

  // const handleRemoveDocument = async (type: string) => {
  //   if (!executedDocumentUrl) return;

  //   try {
  //     await apiService.post(`/rental-schedule/delete-rental-schedule-document/${scheduleData.id}`, {
  //       type: type,
  //     });

  //     setExecutedDocumentUrl("");

  //     toast.success("Document removed successfully");

  //   } catch (err) {
  //     console.error("Failed to remove document", err);
  //     toast.error("Failed to remove document");
  //   }
  // };

  const getDisplayStatus = (scheduleData:any) => {
  
    const today = new Date();
    const lastDate = scheduleData?.config?.buy_back_date
      ? new Date(scheduleData.config.buy_back_date)
      : null;

    scheduleStatus?.toLowerCase() === "draft"
              ? "secondary"
              : scheduleStatus?.toLowerCase() === "approved"
              ? "success"
              : "default";
    const status = scheduleStatus?.toLowerCase();

    // 1. If Draft → show Draft
    if (status === "draft") {
      return "Draft";
    }

    const noaApproved = scheduleData?.noa?.status?.toLowerCase() === "approved";
    const assignLater = String(
      scheduleData.assignMode ??
        scheduleData.lenderAssignment ??
        scheduleData.assignmentMode ??
        ""
    ).toLowerCase() === "assign_later";

    // Approved + (NoA approved or self-funded assign_later): Active / Matured by buy-back date
    if (status === "approved" && (noaApproved || assignLater)) {
      if (lastDate) {
        if (today < lastDate) {
          return "Active";
        }
        return "Matured";
      }
    }

    return status?.charAt(0).toUpperCase() + status?.slice(1);
};

  const displayStatus = getDisplayStatus(scheduleData);

  const assignmentMode = String(
    scheduleData.assignMode ??
      scheduleData.lenderAssignment ??
      scheduleData.assignmentMode ??
      ""
  ).toLowerCase();
  const isAssignLater = !relatedNoA && assignmentMode === "assign_later";

  const regularLine = lines.find((l: ScheduleLine) => !l.isInterim);

  // const tenurePeriodCount = useMemo(
  //   () => lines.filter((l: ScheduleLine) => !l.isInterim).length,
  //   [lines]
  // );

  const formatRentalPeriodDate = (dateInput?: string) => {
    if (!dateInput) return "—";
    const d = parseLocalDateInput(dateInput);
    if (Number.isNaN(d.getTime())) return "—";
    return format(d, "dd MMM yy");
  };

  const getLineDisplayStatus = (line: ScheduleLine): InvoiceStatus | null => {
    const status = line.status?.trim();
    const normalized = status?.toLowerCase();

    if (normalized === "paid") return "Paid";

    const due = parseLocalDateInput(line.dueDate);
    const isPastDue =
      !Number.isNaN(due.getTime()) &&
      startOfDay(due).getTime() <= startOfDay(new Date()).getTime();

    if (isPastDue) return "Overdue";

    if (!status) return null;
    return status as InvoiceStatus;
  };

  const getApproverName = () => {
    if (scheduleStatus !== "Approved" && scheduleStatus !== "approved") return "—";

    const approverId = approvedBy ?? scheduleData.approvedBy;
    const approverName = adminUserList.find(
      (user) => String(user.id) === String(approverId)
    )?.name;
    if (approverName) return approverName;

    const currentUser = getLocalStorageItem("detail");
    const currentUserId = currentUser?.userId ?? currentUser?.id;
    if (
      currentUserId !== undefined &&
      currentUserId !== null &&
      String(currentUserId) === String(approverId)
    ) {
      return currentUser?.name || currentUser?.firstName || "—";
    }

    return "—";
  };

  // const handleAddNoA = async (noa: NoticeOfAssignment) => {
  //   try {
  //     await apiService.post<{ data: NoticeOfAssignment; message?: string }>(
  //       `/noa/create-noa`,
  //       noa as any
  //     );
  //     toast.success("Notice of Assignment created successfully");
  //   } catch (error) {
  //     console.error("Error creating NoA:", error);
  //     toast.error("Failed to create Notice of Assignment");
  //   }
  // };

  return (
    <div className="mx-auto px-2 py-2 space-y-2">
      {/* Header */}
      <section className="pb-6 border-b space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <FileText className="h-7 w-7" />
                Schedule {formatScheduleNumber(scheduleData.displayId)}
              </h1>

              {/* <Badge
                variant={
                  scheduleStatus?.toLowerCase() === "draft"
                    ? "secondary"
                    : scheduleStatus?.toLowerCase() === "approved"
                    ? "success"
                    : "default"
                }
              >
                {(() => {
                  const today = new Date();
                  const lastDate = scheduleData?.config?.buy_back_date
                    ? new Date(scheduleData.config.buy_back_date)
                    : null;

                  const status = scheduleStatus?.toLowerCase();

                  // 1. If Draft → show Draft
                  if (status === "draft") {
                    return "Draft";
                  }

                  // 2. If Approved + NOA exists
                  if (status === "approved" && scheduleData?.noa?.status?.toLowerCase() === "approved") {
                    if (lastDate) {
                      // Future date → Active
                      if (today < lastDate) {
                        return "Active";
                      }
                      // Past date → Matured
                      return "Matured";
                    }
                  }

                  // fallback
                  return status?.charAt(0).toUpperCase() + status?.slice(1);
                })()}
              </Badge> */}
              <Badge variant={
                getStatusColor(displayStatus)}>
                 {displayStatus}
              </Badge>
              
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building className="h-4 w-4" />
                {config.customer.name}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Updated:{" "}
                {new Date(scheduleData.updatedAt).toLocaleDateString("en-IN")}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {scheduleStatus !== "Approved" &&
              scheduleStatus !== "approved" && (
                <>
                  <Button
                    onClick={handleApprove}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                  variant="ghost"
                    onClick={handleEditSchedule}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                </>
              )}

            {!portfolioCalculated && (
              <Button
                variant="outline"
                onClick={() => void handleCalculatePortfolio()}
                disabled={calculatingPortfolio}
                className="flex items-center gap-2"
              >
                {calculatingPortfolio ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <BarChart3 className="h-4 w-4" />
                )}
                Calculate Portfolio
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleVisualize}
              className="flex items-center gap-2 p-5"
            >
              <Eye className="h-4 w-4" />
              Visualise
            </Button>
            <Button
              variant="outline"
              onClick={() => (onBack ? onBack() : navigate("/schedules"))}
            >
              Back
            </Button>
          </div>
        </div>
      </section>

      {/* KPI Dashboard - Unified Panel */}
      <section className="border rounded-lg bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-5 divide-x divide-border">
          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Total Asset Value
            </div>
            <div className="text-2xl font-bold">
              ₹
              {Math.round(
                assets
                  .map((a: any) => a.total || 0)
                  .reduce((sum: any, val: any) => sum + val, 0)
              ).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-muted-foreground">Base + GST</div>
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Base Amount
            </div>
            <div className="text-2xl font-bold">
              ₹
              {lines
                .find((l: any) => !l.isInterim)
                ?.baseRentRounded?.toLocaleString("en-IN") || "—"}
            </div>
            <div className="text-xs text-muted-foreground">
              Per {config.frequency.toLowerCase()} period
            </div>
          </div>

          <div
            className="p-6 space-y-1 hover:bg-muted/50 transition-colors cursor-pointer"
            
          >
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Disbursement Amount
            </div>
            {relatedNoA ? (
              <>
                <div className="text-2xl font-bold">
                  ₹{Math.round(relatedNoA.pvTotal).toLocaleString("en-IN")}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  {relatedNoA.lender}
                </div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-muted-foreground">
                  ₹0
                </div>
                <div className="text-xs text-muted-foreground">
                  No assignment created
                </div>
              </>
            )}
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Upfront Received
            </div>
            <div
              className={`text-2xl font-bold ${
                relatedNoA?.upfrontRevenue != null
                  ? relatedNoA.upfrontRevenue < 0
                    ? "text-red-600"
                    : relatedNoA.upfrontRevenue > 0
                      ? "text-emerald-600"
                      : ""
                  : ""
              }`}
            >
              ₹
              {relatedNoA?.upfrontRevenue != null
                ? relatedNoA.upfrontRevenue.toLocaleString("en-IN")
                : upfront?.totalRounded?.toLocaleString("en-IN") || "—"}
            </div>
            {/* <div className="text-xs text-muted-foreground">
              SD + Processing Fee
            </div> */}
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Total Receivables
            </div>
            <div className="text-2xl font-bold">
              ₹{gstTotals.grandTotal.toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-muted-foreground">
              All periods incl. GST
            </div>
          </div>
        </div>
      </section>

      {/* Bill To / Ship To and Rental Schedule Info combined - Only show if approved with addresses */}

      {/* Main Content - Tabs */}
      <Tabs defaultValue="schedule-details" className="space-y-4">
        <TabsList className="inline-flex h-auto w-full max-w-2xl items-center rounded-lg border bg-muted/50 p-1">
          <TabsTrigger value="schedule-details" className="flex-1 gap-2">
            <FileText className="h-4 w-4" />
            Schedule Details
          </TabsTrigger>
          <TabsTrigger value="rental-schedule" className="flex-1 gap-2">
            <Calendar className="h-4 w-4" />
            Rental Schedule
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex-1 gap-2">
            <FileCheck className="h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schedule-details">
          <>
          
        
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Asset Details */}
        <section className="space-y-4">
          <div className="flex items-center justify-between pb-3 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Package className="h-5 w-5" />
              Asset Details
            </h2>
            {/* <Button
              variant="outline"
              size="sm"
              onClick={() => setAssetManagementOpen(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Assets
            </Button> */}
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-[550px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
                <TableRow className="bg-muted/50">
                  <TableHead className="bg-muted/50">Asset</TableHead>
                  <TableHead className="text-right bg-muted/50">Base</TableHead>
                  <TableHead className="text-right bg-muted/50">Qty</TableHead>
                  <TableHead className="text-right bg-muted/50">GST</TableHead>
                  <TableHead className="text-right bg-muted/50">Total</TableHead>
                  <TableHead className="bg-muted/50">PO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset: any) => (
                  <TableRow key={asset.assetLineId || asset.assetId}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{asset.item}</div>
                        <div className="text-sm text-muted-foreground">
                          Invoice No: {asset.invoiceNo}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      ₹{asset.basePrice.toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.quantity || 1}
                    </TableCell>
                    <TableCell className="text-right">
                      {asset.gstRate}%
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{asset.total.toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {`PO-` +
                        (asset?.poNumber?.toString().padStart(4, "0") ?? "—")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>
        </section>

        {/* Right Column - Computation Details */}
        <section className="space-y-4">
          <div className="pb-3 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Computation Details
            </h2>
          </div>

          <div className="space-y-0 border rounded-lg divide-y divide-border/60">
            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                <span>Total Asset Value</span>
              </div>
              <div className="font-semibold">
                ₹
                {Math.round(
                  assets
                    .map((a: any) => a.total || 0)
                    .reduce((sum: any, val: any) => sum + val, 0)
                ).toLocaleString("en-IN")}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Repeat className="h-4 w-4" />
                <span>Frequency</span>
              </div>
              <div className="font-semibold">
                {config.frequency.charAt(0).toUpperCase() +
                  config.frequency.slice(1)}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Tenure</span>
              </div>
              <div className="font-semibold">
                {config.tenureMonths} Months
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BarChart3 className="h-4 w-4" />
                <span>{config.frequency === "monthly" ? "PTPM" : "PTPQ"}</span>
              </div>
              <div className="font-semibold">
                {config.frequency === "monthly" ? config.ptpm : config.ptpm}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                <span>Discounting Factor</span>
              </div>
              <div className="font-semibold">
                {config.discountingFactor}%
              </div>
              
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Receipt className="h-4 w-4" />
                <span>Processing Fee</span>
              </div>
              <div className="font-semibold">
                {config.processingFeePercent}% (₹
                {upfront?.processingFeeRounded?.toLocaleString("en-IN") || 0})
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Security Deposit</span>
              </div>
              <div className="font-semibold">
                {config.securityDepositPercent}% (₹
                {upfront?.securityDepositAmountRounded?.toLocaleString(
                  "en-IN"
                ) || 0}
                )
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Coins className="h-4 w-4" />
                  <span>Buyback</span>
                </div>

                <div>

                <div className="font-semibold">
                  {config.buy_back_percentage}% (₹{Math.ceil(Number(config.buy_back_total_amount) || 0).toLocaleString("en-IN")})
                </div> 
                </div>
               
              </div>

              <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Buyback Date</span>
                </div>
                <div className="font-semibold">
                  {config.buy_back_date ? formatDisplayDate(config.buy_back_date) : ""}
                </div>
              </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <IndianRupee className="h-4 w-4" />
                <span>Base Rental (excl. GST)</span>
              </div>
              <div className="font-semibold">
                ₹
                {(lines.find((l: any) => !l.isInterim)?.baseRentRounded).toLocaleString("en-IN") || 0}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Percent className="h-4 w-4" />
                <span>GST on Base Rental</span>
              </div>
              <div className="font-semibold">
                ₹
                {(
                  (lines.find((l: any) => !l.isInterim)?.gstRaw ?? 0)
                ).toLocaleString("en-IN")}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calculator className="h-4 w-4" />
                <span>Total Rental</span>
              </div>
              <div className="text-lg font-bold text-green-600">
                ₹
                {(lines.find((l: any) => !l.isInterim)?.baseRentRounded + (lines.find((l: any) => !l.isInterim)?.gstRaw ?? 0)).toLocaleString("en-IN") || 0}
              </div>
            </div>
          </div>
        </section>
        
      </div>


      {/* Self-funded assignment — assign later, no NoA yet */}
      {isAssignLater && (
        <section className="border border-amber-200/70 rounded-lg bg-amber-50/60 p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 pb-3 border-b border-amber-200/60">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Assignment Status
                </h2>
                <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
                  Self-Funded
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground max-w-3xl">
                This schedule is not assigned to any lender. All invoices are raised by and collected directly to Astrafin.
              </p>
            </div>
            {/* <NoAGeneratorDrawer
              approvedSchedules={[scheduleData]}
              addNoA={handleAddNoA}
            >
              <Button className="shrink-0">
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Assign to Lender Now
              </Button>
            </NoAGeneratorDrawer> */}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Mode
              </div>
              <div className="font-semibold">Self-Funded (Assign Later)</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Payee
              </div>
              <div className="flex items-center gap-2">
                <Avatar className="rounded-lg" style={{ height: "30px", width: "45px" }}>
                  <AvatarImage
                    src={astrafinLogo}
                    alt="Astrafin"
                    className="object-contain"
                  />
                </Avatar>
                <div className="font-semibold">Astrafin</div>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Approved On
              </div>
              <div className="font-semibold">
                {scheduleData.approvedDate
                  ? formatDisplayDate(scheduleData.approvedDate)
                  : scheduleData.updatedAt
                    ? formatDisplayDate(scheduleData.updatedAt)
                    : "—"}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Approved By
              </div>
              <div className="font-semibold">{getApproverName()}</div>
            </div>
          </div>
        </section>
      )}

      {/* NoA Status - Only show if NoA exists */}
      {relatedNoA && (
        <section className="border rounded-lg bg-primary/5 p-6 space-y-4">
          <div className="pb-3 border-b border-primary/10">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              Assignment Status
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Notice of Assignment details and lender funding information
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)_minmax(0,1fr)_minmax(0,1fr)] gap-6">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                NoA ID
              </div>
              <Button
                variant="link"
                className="h-auto p-0 font-semibold text-base hover:underline"
                
              >
                {relatedNoA.noaId}
              </Button>
            </div>
            <div className="space-y-1 min-w-0 md:col-start-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Lender
              </div>
              <div className="flex items-center gap-2 min-w-0">
                {relatedNoA.lenderAvatar ? (
                  <Avatar className="rounded-lg shrink-0" style={{ height: "30px", width: "45px" }}>
                    <AvatarImage
                      src={
                        relatedNoA.lenderAvatar.startsWith("http")
                          ? relatedNoA.lenderAvatar
                          : `${import.meta.env.VITE_API_BASE_URL}/${relatedNoA.lenderAvatar}`
                      }
                      alt={relatedNoA.lender || relatedNoA.lenderName || "Lender"}
                      className="object-contain"
                    />
                  </Avatar>
                ) : null}
                <div className="font-semibold break-words">{relatedNoA.lender}</div>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                PV Amount (Disbursement)
              </div>
              <div className="font-semibold">
                ₹{Math.round(relatedNoA.pvTotal).toLocaleString("en-IN")}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Status
              </div>
              <Badge
                variant={
                  relatedNoA.status === "Assigned"
                    ? "success"
                    : relatedNoA.status === "Approved"
                      ? "default"
                      : "secondary"
                }
              >
                {relatedNoA.status}
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Assignment Date
              </div>
              <div className="font-semibold">
                {formatDisplayDate(relatedNoA.assignmentDate)}
              </div>
            </div>
            <div className="space-y-1 min-w-0 md:col-start-2">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Funding Mode
              </div>
              <div className="font-semibold break-words">{config.fundingType}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Discount Rate
              </div>
              <div className="font-semibold">
                {relatedNoA.discountRate}% p.a.
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                Selected Periods
              </div>
              <div className="font-semibold">
                {relatedNoA.selectedRentals.length} periods
              </div>
            </div>
          </div>

          {/* NACH Setup */}
          {/* <div className="mt-6 pt-6 border-t border-primary/10">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <IndianRupee className="h-4 w-4" />
                    <h3 className="font-semibold">NACH Setup</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Configure National Automated Clearing House for automated
                    payment collection by lender
                  </p>
                </div>
                <Switch
                  checked={nachSetupEnabled}
                  onCheckedChange={(checked:any) => {
                    if (checked) {
                      setNachSetupModalOpen(true);
                    } else {
                      setNachSetupEnabled(false);
                      setNachFromInvoiceId(null);
                      toast.error("NACH setup has been disabled for this schedule.");
                    }
                  }}
                />
              </div>
              {nachSetupEnabled && nachFromInvoiceId && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <div className="font-medium text-blue-900">
                    NACH Active from: {nachFromInvoiceId}
                  </div>
                  <p className="text-blue-700 text-xs mt-1">
                    All lender-assigned rentals from this invoice onwards will
                    be marked as NACH-enabled
                  </p>
                </div>
              )}
            </div> */}
        </section>
      )}

      {/* Invoice Summary - Only show if schedule is approved */}
   

      {/* Rental Schedule Info with Bill To / Ship To */}
      <section className="space-y-4">
        <div className="pb-3 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Rental Schedule Info
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Schedule metadata, references, and billing details
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Side - Bill To & Ship To */}
          {(scheduleStatus === "Approved" || scheduleStatus === "approved") &&
            (scheduleData.address.billTo ||
              billToAddress ) && (
              <div className="border rounded-lg p-4 bg-muted/20 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  Billing & Shipping
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {/* Bill To */}
                  <div className="p-3 bg-background rounded-md border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide mb-1">
                      <Building className="h-3 w-3" />
                      Bill To
                    </div>
                    <div className="font-medium text-sm">
                      {(billToAddress || (scheduleData as any).billTo)
                        ?.label || "—"}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {(billToAddress || (scheduleData as any).billTo)
                        ?.address || "—"}
                    </p>
                    {(billToAddress || (scheduleData as any).billTo)
                      ?.gstin && (
                        <p className="text-xs text-muted-foreground mt-1">
                          GSTIN:{" "}
                          {
                            (billToAddress || (scheduleData as any).billTo)
                              ?.gstin
                          }
                        </p>
                      )}
                  </div>

                  {/* <div className="p-3 bg-background rounded-md border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide mb-1">
                      <MapPin className="h-3 w-3" />
                      Ship To
                    </div>
                    <div className="font-medium text-sm">
                      {(shipToAddress || (scheduleData as any).shipTo)
                        ?.label || "—"}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {(shipToAddress || (scheduleData as any).shipTo)
                        ?.address || "—"}
                    </p>
                    {(shipToAddress || (scheduleData as any).shipTo)
                      ?.gstin && (
                        <p className="text-xs text-muted-foreground mt-1">
                          GSTIN:{" "}
                          {
                            (shipToAddress || (scheduleData as any).shipTo)
                              ?.gstin
                          }
                        </p>
                      )}
                  </div> */}
                </div>
              </div>
            )}

          {/* Right Side - Schedule Details */}
          <div
            className={`border rounded-lg p-4 bg-muted/20 ${!(
              (scheduleStatus === "Approved" ||
                scheduleStatus === "approved") &&
              (billToAddress ||
                (scheduleData as any).billTo)
            )
              ? "lg:col-span-2"
              : ""
              }`}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Calendar className="h-3 w-3" />
                  <span>Created at</span>
                </div>
                <div className="font-medium text-sm">
                  {scheduleData.createdAt
                    ? formatDisplayDate(scheduleData.createdAt)
                    : "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <User className="h-3 w-3" />
                  <span>Created by</span>
                </div>
                <div className="font-medium text-sm">
                  {adminUserList.find(
                    (user) => user.id === scheduleData.createdBy
                  )?.name || "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <UserCheck className="h-3 w-3" />
                  <span>Approved by</span>
                </div>
                <div className="font-medium text-sm">
                  {scheduleStatus === "Approved" || scheduleStatus === "approved"
                    ? (() => {
                      const approverName = adminUserList.find(
                        (user) => String(user.id) === String(approvedBy)
                      )?.name;
                      if (approverName) return approverName;

                      const currentUser = getLocalStorageItem("detail");
                      const currentUserId = currentUser?.userId ?? currentUser?.id;
                      if (
                        currentUserId !== undefined &&
                        currentUserId !== null &&
                        String(currentUserId) === String(approvedBy)
                      ) {
                        return currentUser?.name || currentUser?.firstName || "—";
                      }
                      return "—";
                    })()
                    : "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Hash className="h-3 w-3" />
                  <span>Schedule ID</span>
                </div>
                <div className="font-medium text-sm">
                  {scheduleData.rentalScheduleCode}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <FileText className="h-3 w-3" />
                  <span>Termsheet ID</span>
                </div>
                <div className="font-medium text-sm">
                  {scheduleData?.config?.termSheetCode}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Package className="h-3 w-3" />
                  <span>Product Type</span>
                </div>
                <div className="font-medium text-sm">
                  {distinctProductTypes.length > 0
                    ? distinctProductTypes.join(", ")
                    : "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Hash className="h-3 w-3" />
                  <span>Requirement ID</span>
                </div>
                <div className="font-medium text-sm">
                  {distinctRequirementIds.length > 0
                    ? distinctRequirementIds.join(", ")
                    : "—"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Link2 className="h-3 w-3" />
                  <span>NoA ID</span>
                </div>
                {relatedNoA ? (
                  <Button
                    variant="link"
                    className="h-auto p-0 font-medium text-sm hover:underline"
                    onClick={() => navigate(`/noa/${relatedNoA.noaId}`)}
                  >
                    {relatedNoA.noaId}
                  </Button>
                ) : (
                  <div className="font-medium text-sm text-muted-foreground">
                    Not created
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Calendar className="h-3 w-3" />
                  <span>Commencement Date</span>
                </div>
                <div className="font-medium text-sm">
                  {formatDisplayDate(config.commencementDate)}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Clock className="h-3 w-3" />
                  <span>Payment Cycle Day</span>
                </div>
                <div className="font-medium text-sm">
                  {config.paymentCycleDay
                    ? `${formatOrdinal(
                      config.paymentCycleDay
                    )} of every month`
                    : "-"}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <Hash className="h-3 w-3" />
                  <span>Rental Schedule Number</span>
                </div>
                <div className="font-medium text-sm">
                  {formatScheduleNumber(scheduleData?.displayId)}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                  <FileSignature className="h-3 w-3" />
                  <span>MRA Signed Date</span>
                </div>
                <div className="font-medium text-sm">
                   {scheduleData?.config?.mraSignedDate
                    ? formatDisplayDate(scheduleData?.config?.mraSignedDate)
                    : "—"}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      </>
        </TabsContent>

        <TabsContent value="rental-schedule">
          <section className="space-y-4">
            {/* <div className="pb-3 border-b">
              <h2 className="text-lg font-semibold">All Tenure Periods</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {tenurePeriodCount} period{tenurePeriodCount === 1 ? "" : "s"} •{" "}
                {config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1)}{" "}
                billing
              </p>
            </div> */}

            <div className="border rounded-lg ">
              <div className="">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
                    <TableRow className="bg-muted/50">
                      <TableHead className="bg-muted/50">Period</TableHead>
                      <TableHead className="bg-muted/50">From</TableHead>
                      <TableHead className="bg-muted/50">To</TableHead>
                      <TableHead className="bg-muted/50">Due Date</TableHead>
                      <TableHead className="text-right bg-muted/50">Base Rental</TableHead>
                      <TableHead className="text-right bg-muted/50">
                        GST ({regularLine?.gstRate ?? 18}%)
                      </TableHead>
                      <TableHead className="text-right bg-muted/50">TDS</TableHead>
                      <TableHead className="text-right bg-muted/50">Total</TableHead>
                      <TableHead className="bg-muted/50 text-center">Invoice</TableHead>
                      <TableHead className="bg-muted/50 text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className="py-10 text-center text-sm text-muted-foreground"
                        >
                          No rental periods available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      lines.map((line: ScheduleLine) => {
                        const lineStatus = getLineDisplayStatus(line);
                        const periodLabel = line.isInterim
                          ? `${line.periodNo} (Interim)`
                          : String(line.periodNo);

                        return (
                          <TableRow key={line.scheduleLineId}>
                            <TableCell className="font-medium">{periodLabel}</TableCell>
                            <TableCell>{formatRentalPeriodDate(line.fromDate)}</TableCell>
                            <TableCell>{formatRentalPeriodDate(line.toDate)}</TableCell>
                            <TableCell>{formatRentalPeriodDate(line.dueDate)}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              ₹{line.baseRentRounded.toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              ₹{(line.gstRaw ?? 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              ₹{(line.tdsRounded ?? 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-right font-medium tabular-nums">
                              ₹{(line.baseRentRounded + (line.gstRaw ?? 0) - (line.tdsRounded ?? 0)).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-center">
                              {line.rsInvoiceCode ? (
                                line.invoicePdfUrl ? (
                                  <a
                                    href={line.invoicePdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary text-sm font-medium hover:underline"
                                  >
                                    {line.rsInvoiceCode}
                                  </a>
                                ) : (
                                  <span className="text-primary-dark text-sm font-medium">
                                    {line.rsInvoiceCode}
                                  </span>
                                )
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {lineStatus ? (
                                <Badge
                                  variant="outline"
                                  className={`gap-1 ${getInvoiceStatusColor(lineStatus)} text-center`}
                                >
                                  <Clock className="h-3 w-3" />
                                  {lineStatus}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-center">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="documents">
          <ScheduleDocumentsTab
            rentalScheduleId={scheduleData?.id}
            customerId={scheduleData?.config?.customer?.customerId || ""}
            onViewSchedule={() => openSystemDocDrawer("rental_schedule")}
            onViewLeasingDocs={() => setLeasingOpen(true)}
            onViewSignatories={() => setEditSignatoriesOpen(true)}
          />
        </TabsContent>
      </Tabs>


      {/* Modals */}
      <SchedulePreviewModal
        open={visualizeOpen}
        onOpenChange={setVisualizeOpen}
        lines={lines}
        upfront={upfront}
        config={config}
        assets={assets}
        onConfirm={() => setVisualizeOpen(false)}
      />

      <EditSignatoriesModal
        open={editSignatoriesOpen}
        onOpenChange={setEditSignatoriesOpen}
        ourSignatory={ourSignatory}
        customerSignatory={customerSignatory}
        onSave={handleSaveSignatories}
      />

      <AssetManagementModal
        open={assetManagementOpen}
        onOpenChange={setAssetManagementOpen}
        assets={assets}
        onSave={handleSaveAssets}
      />

      <SchedulePdfDrawer
        open={systemDocDrawerOpen}
        onOpenChange={setSystemDocDrawerOpen}
        scheduleId={scheduleData?.id || ""}
        documentType={selectedDocType}
        schedule={scheduleData}
      />

      <ScheduleDocumentDrawer
        open={uploadedDocDrawerOpen}
        onOpenChange={setUploadedDocDrawerOpen}
        document={
          selectedDocType ? getUploadedDocument(selectedDocType) : undefined
        }
      />

      <ApproveScheduleModal
        open={approveModalOpen}
        onOpenChange={setApproveModalOpen}
        customerName={config.customer.name}
        onConfirm={handleConfirmApproval}
        customerAddresses={customerAddresses}
        customerId={
          schedule?.config?.customer?.customerId || ""
        }
        onAddressesChange={onAddressesChange}
        scheduleLines={lines}
        tdsRatePercent={
          config.tdsRatePercent ?? config.TDS_rate ?? scheduleData?.TDS_rate
        }
      />

      {/* Edit Schedule Drawer */}
      <ScheduleGeneratorDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        assets={purchaseOrderAssetList}
        termSheetList={termSheetList}
        termSheetId={scheduleData?.config?.termSheetId}
        preselectedAssets={scheduleData?.assets}
        schedule={scheduleData}
        onPreview={(l, u, m, termSheetId) => {
          setEditLines(l);
          setEditUpfront(u);
          setEditMeta(m);
          setTermSheetId(termSheetId);
          setEditPreviewOpen(true);
        }}
        tdsRateList={tdsRateList}
      />

      {/* Edit Schedule Preview Modal */}
      <SchedulePreviewModal
        open={editPreviewOpen}
        onOpenChange={setEditPreviewOpen}
        lines={editLines ?? []}
        upfront={editUpfront}
        config={editMeta?.config}
        assets={editMeta?.assets}
        onConfirm={handleUpdateSchedule}
        mode="edit"
      />

      <LeasingDocModal 
      open={leasingOpen}
      onOpenChange={setLeasingOpen}
      scheduleId= {scheduleData?.id || ""}
        executed_date = {scheduleData?.executed_date}
      />
    </div>
  );
};

